import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogBody,
    DialogFooter,
    Typography,
    Button,
    IconButton,
} from '@material-tailwind/react';
import {
    XMarkIcon,
    CameraIcon,
    ChatBubbleLeftRightIcon,
    BookOpenIcon,
    ShoppingCartIcon,
} from '@heroicons/react/24/outline';
import { useLanguage } from '../contexts/LanguageContext';
import { languages } from '../languages';
import { brandLogo as logo, brandName, isMaisterchefGermanTheme } from '../utils/branding';

const STORAGE_PREFIX = 'souschaif_feature_welcome_';

const getTranslationLanguage = (language) => {
    if (language === 'befr' || language === 'be-fr') return 'french';
    if (language === 'benl' || language === 'be-nl') return 'nederlands';
    if (language === 'bh-ar' || language === 'bahrain-arabic') return 'arabic';
    if (language === 'bh-en' || language === 'bahrain-english') return 'english';
    if (language === 'uae-ar' || language === 'uae-arabic') return 'arabic';
    if (language === 'uae-en' || language === 'uae-english') return 'english';
    if (language === 'sa-ar' || language === 'sa-arabic') return 'arabic';
    if (language === 'sa-en' || language === 'sa-english') return 'english';
    if (language === 'kw-ar' || language === 'kw-arabic') return 'arabic';
    if (language === 'kw-en' || language === 'kw-english') return 'english';
    if (language === 'qa-ar' || language === 'qa-arabic') return 'arabic';
    if (language === 'qa-en' || language === 'qa-english') return 'english';
    if (language === 'om-ar' || language === 'om-arabic') return 'arabic';
    if (language === 'om-en' || language === 'om-english') return 'english';
    if (language === 'iq-ar' || language === 'iq-arabic') return 'arabic';
    if (language === 'iq-en' || language === 'iq-english') return 'english';
    if (language === 'jo-ar' || language === 'jo-arabic') return 'arabic';
    if (language === 'jo-en' || language === 'jo-english') return 'english';
    if (language === 'lb-ar' || language === 'lb-arabic') return 'arabic';
    if (language === 'lb-en' || language === 'lb-english') return 'english';
    if (language === 'sy-ar' || language === 'sy-arabic') return 'arabic';
    if (language === 'sy-en' || language === 'sy-english') return 'english';
    if (language === 'ps-ar' || language === 'ps-arabic') return 'arabic';
    if (language === 'ps-en' || language === 'ps-english') return 'english';
    if (language === 'ye-ar' || language === 'ye-arabic') return 'arabic';
    if (language === 'ye-en' || language === 'ye-english') return 'english';
    if (language === 'eg-ar' || language === 'eg-arabic') return 'arabic';
    if (language === 'eg-en' || language === 'eg-english') return 'english';
    if (language === 'pl-pl') return 'polish';
    if (language === 'cyprus' || language === 'cy' || language === 'el-cy') return 'greek';
    return language;
};

const FeatureWelcomeDialog = ({ isLoggedIn }) => {
    const { language } = useLanguage();
    const translationLanguage = getTranslationLanguage(language);
    const t = languages[translationLanguage] || languages.english;

    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!isLoggedIn) {
            setOpen(false);
            return;
        }

        const userId = localStorage.getItem('userId');
        if (!userId) return;

        const storageKey = `${STORAGE_PREFIX}${userId}`;
        if (!localStorage.getItem(storageKey)) {
            setOpen(true);
        }
    }, [isLoggedIn]);

    const dismiss = () => {
        const userId = localStorage.getItem('userId');
        if (userId) {
            localStorage.setItem(`${STORAGE_PREFIX}${userId}`, 'true');
        }
        setOpen(false);
    };

    const features = [
        {
            Icon: CameraIcon,
            title: t.featureWelcomePhotoTitle || t.pictureIngredients || t.uploadPhoto,
            description: t.featureWelcomePhotoDescription || t.photoTip,
            accent: 'bg-green-500 text-white',
            highlighted: true,
        },
        {
            Icon: ChatBubbleLeftRightIcon,
            title: t.chatWithSousChaif || t.needHelpWithRecipe || 'AI Chef Chat',
            description: t.featureWelcomeChatDescription || t.chatBotIntro,
            accent: 'bg-green-100 text-green-700',
        },
        {
            Icon: BookOpenIcon,
            title: t.recipeLibraryTitle || 'Recipe Library',
            description: t.featureWelcomeLibraryDescription || t.viewAllRecipes,
            accent: 'bg-blue-100 text-blue-700',
        },
        {
            Icon: ShoppingCartIcon,
            title: t.smartShoppingList || t.shoppingList || 'Shopping List',
            description: t.smartShoppingListDescription || t.featureWelcomeShoppingDescription,
            accent: 'bg-purple-100 text-purple-700',
        },
    ];

    return (
        <Dialog
            open={open}
            handler={dismiss}
            size="md"
            className={`max-h-[92vh] !flex !flex-col overflow-hidden ${isMaisterchefGermanTheme(language) ? 'mc-de-dialog' : ''}`}
        >
            <DialogBody className="p-0 flex-1 min-h-0 overflow-y-auto overscroll-contain">
                <div className="relative bg-gradient-to-br from-green-50 via-white to-white px-6 pt-8 pb-4 sm:px-8">
                    <IconButton
                        variant="text"
                        size="sm"
                        className="!absolute ltr:right-3 rtl:left-3 top-3 z-10 rounded-full text-gray-500 hover:bg-gray-100"
                        onClick={dismiss}
                        aria-label={t.close || 'Close'}
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </IconButton>

                    <div className="flex flex-col items-center text-center mb-6 px-2 pt-2">
                        <img src={logo} alt={`${brandName} logo`} className="h-14 w-auto mb-4" />
                        <Typography variant="h4" className="text-gray-900 font-bold mb-3 leading-snug">
                            {t.featureWelcomeTitle || `Welcome to ${brandName}!`}
                        </Typography>
                        <Typography variant="paragraph" className="text-gray-600 max-w-md leading-relaxed">
                            {t.featureWelcomeSubtitle || t.welcomeMessage}
                        </Typography>
                    </div>

                    <div className="space-y-4">
                        {features.map(({ Icon, title, description, accent, highlighted }, index) => (
                            <div
                                key={index}
                                className={`flex gap-4 items-start rounded-xl p-4 shadow-sm ${
                                    highlighted
                                        ? 'border-2 border-green-500 bg-green-50 ring-1 ring-green-200'
                                        : 'border border-gray-100 bg-white'
                                }`}
                            >
                                <div className={`flex-shrink-0 rounded-xl p-3 ${accent}`}>
                                    <Icon className="h-6 w-6" />
                                </div>
                                <div className="flex-1 ltr:text-left rtl:text-right min-w-0">
                                    <Typography variant="h6" className="text-gray-900 mb-1.5 font-semibold leading-snug">
                                        {title}
                                    </Typography>
                                    <Typography variant="small" className="text-gray-600 leading-relaxed block">
                                        {description}
                                    </Typography>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </DialogBody>

            <DialogFooter className="shrink-0 border-t border-gray-200 bg-white px-6 py-4 sm:px-8">
                <Button
                    color="green"
                    size="lg"
                    fullWidth
                    className="normal-case font-semibold shadow-md"
                    onClick={dismiss}
                >
                    {t.featureWelcomeGotIt || t.getStarted || t.next || 'Got it'}
                </Button>
            </DialogFooter>
        </Dialog>
    );
};

export default FeatureWelcomeDialog;
